import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ParsedJWTToken } from '../types/auth';
import { processB2CAuthCode, getStoredToken } from '../utils/authUtils';

export const useJWTToken = () => {
  const { search, hash } = useLocation();
  const navigate = useNavigate();
  const [parsedToken, setParsedToken] = useState<ParsedJWTToken | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processToken = async () => {
      // Check if we already have a token
      const existingToken = getStoredToken();
      if (existingToken) {
        setParsedToken(existingToken);
        return;
      }

      // Check for auth code in URL
      const queryParams = new URLSearchParams(search);
      const hashParams = new URLSearchParams(hash.replace("#", ""));
      const authCode = queryParams.get("code") || hashParams.get("code");

      if (authCode && !isProcessing) {
        setIsProcessing(true);
        setError(null);

        try {
          console.log('Processing B2C authorization code...');
          const token = await processB2CAuthCode(authCode);

          if (token) {
            setParsedToken(token);
            // Clean URL after successful processing
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);

            // Navigate immediately - don't wait for background processes
            navigate('/external-home');
          } else {
            setError('Failed to process authentication token');
          }
        } catch (e) {
          console.error('Token processing failed:', e);
          setError('Authentication failed. Please try again.');
        } finally {
          setIsProcessing(false);
        }
      }
    };

    processToken();
  }, [search, hash, navigate, isProcessing]);

  return { parsedToken, isProcessing, error };
};
